import * as React from 'react'
import { cn } from '@/utils/cn'
import type { OAuthProvider } from '@/types'

/**
 * Sottovo v2 ソーシャルログインボタン群。
 *
 * 既存 `components/auth/SocialLoginButtons` の onLogin 契約を踏襲する:
 *   onLogin(provider) → 呼び出し側で oauthService.startOAuthFlow(provider) を実行。
 *
 * 見た目のみ v2 トークン (secondary ボタン) に統一。ブランドアイコンは公式配色を保持。
 */

// ========== ブランドアイコン (公式配色を保持) ==========

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M19.6 10.23c0-.68-.06-1.36-.18-2H10v3.79h5.4a4.62 4.62 0 01-2 3.04v2.52h3.22C18.44 15.95 19.6 13.28 19.6 10.23z"
        fill="#4285F4"
      />
      <path
        d="M10 20c2.7 0 4.97-.9 6.62-2.42l-3.22-2.52c-.9.6-2.04.96-3.4.96-2.61 0-4.82-1.76-5.61-4.13H1.07v2.6A9.99 9.99 0 0010 20z"
        fill="#34A853"
      />
      <path
        d="M4.39 11.89A5.97 5.97 0 014.07 10c0-.65.11-1.29.32-1.89V5.51H1.07A9.99 9.99 0 000 10c0 1.61.38 3.14 1.07 4.49l3.32-2.6z"
        fill="#FBBC05"
      />
      <path
        d="M10 3.98a5.42 5.42 0 013.83 1.5L16.69 2.6A9.62 9.62 0 0010 0 9.99 9.99 0 001.07 5.51l3.32 2.6C5.18 5.74 7.39 3.98 10 3.98z"
        fill="#EA4335"
      />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="8.5" height="8.5" fill="#F25022" />
      <rect x="10.5" y="1" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="1" y="10.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="10.5" y="10.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 0C4.477 0 0 4.477 0 10c0 4.418 2.865 8.166 6.839 9.489.5.09.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0110 4.836c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C17.138 18.163 20 14.418 20 10c0-5.523-4.477-10-10-10z"
      />
    </svg>
  )
}

// ========== 設定 ==========

interface ProviderConfig {
  id: OAuthProvider
  label: string
  Icon: React.FC
}

const PROVIDER_CONFIGS: ProviderConfig[] = [
  { id: 'google', label: 'Google で続行', Icon: GoogleIcon },
  { id: 'microsoft', label: 'Microsoft で続行', Icon: MicrosoftIcon },
  { id: 'github', label: 'GitHub で続行', Icon: GitHubIcon },
]

export interface SocialLoginButtonsV2Props {
  /** プロバイダ選択時に呼ばれる。呼び出し側で startOAuthFlow(provider) を実行する。 */
  onLogin: (provider: OAuthProvider) => void
  /** OAuth フロー開始中かどうか。 */
  loading?: boolean
  /** ローディング中のプロバイダ。スピナー表示の対象。 */
  loadingProvider?: OAuthProvider | null
  /** メールフォーム送信中などで全ボタンを無効化したい場合。 */
  disabled?: boolean
  /**
   * 表示するプロバイダ ID の配列。
   * 未指定時は従来どおり全プロバイダ（google/microsoft/github）を表示（後方互換）。
   * 指定時は配列に含まれる ID のボタンのみ表示する。
   */
  providers?: OAuthProvider[]
  /**
   * プロバイダ一覧の取得中かどうか。true のときスケルトンを表示しレイアウトシフトを防ぐ。
   */
  providersLoading?: boolean
}

function Spinner() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle
        cx="9"
        cy="9"
        r="7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="36"
        strokeDashoffset="10"
      />
    </svg>
  )
}

export function SocialLoginButtonsV2({
  onLogin,
  loading = false,
  loadingProvider = null,
  disabled = false,
  providers,
  providersLoading = false,
}: SocialLoginButtonsV2Props) {
  const visibleConfigs =
    providers === undefined
      ? PROVIDER_CONFIGS
      : PROVIDER_CONFIGS.filter((c) => providers.includes(c.id))

  // providers が明示的な空配列の場合（API 成功・設定 0 件）は何も描画しない。
  // undefined との区別: undefined = 後方互換で全表示 / [] = 表示するものなし。
  if (providers !== undefined && providers.length === 0 && !providersLoading) {
    return null
  }

  if (providersLoading) {
    // providers 指定があり 1 件以上ならその件数、それ以外（undefined / []）はデフォルト 2。
    // providers=[] は「ロード中で件数未確定」を意味するため 2 をデフォルトとする。
    // 注意: `providers?.length ?? 2` では [] → length=0、0 ?? 2 = 0（?? は null/undefined のみ）
    // になりスケルトン 0 件になるバグがあるため、length > 0 の条件で分岐する。
    const skeletonCount = providers && providers.length > 0 ? providers.length : 2
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="social-login-skeleton"
        role="status"
        aria-busy="true"
        aria-label="ログインオプションを読み込み中"
      >
        {Array.from({ length: skeletonCount }, (_, i) => (
          <div
            key={i}
            aria-hidden="true"
            className="min-h-11 w-full animate-pulse rounded-v2-md border border-v2-border bg-v2-surface opacity-60"
          />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {visibleConfigs.map(({ id, label, Icon }) => {
        const isThisLoading = loading && loadingProvider === id
        const isDisabled = disabled || loading

        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              if (!isDisabled) onLogin(id)
            }}
            disabled={isDisabled}
            aria-label={label}
            className={cn(
              'inline-flex min-h-11 w-full items-center justify-center gap-2.5 rounded-v2-md border border-v2-border bg-v2-surface px-4 text-[15px] font-medium text-v2-ink',
              'transition-colors hover:border-v2-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
          >
            {isThisLoading ? <Spinner /> : <Icon />}
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )
}
